import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useMemo, useState} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGateAccess } from '../hooks/useGateAccess';
import ComponentControls from './ComponentControls';
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';

const ICON_PATHS = {
    instagram:  '/icons/icons8-instagram-50.png',
    facebook:   '/icons/icons8-facebook-50.png',
    twitter:    '/icons/icons8-x-50.png',
    threads:    '/icons/icons8-waluigi-50.png',
    github:     '/icons/icons8-github-50.png',
    linkedin:   '/icons/icons8-linkedin-50.png',
    medium:     '/icons/icons8-medium-50.png',
    pinterest:  '/icons/icons8-pinterest-50.png',
    reddit:     '/icons/icons8-reddit-50.png',
    youtube:    '/icons/icons8-youtube-50.png',
    tiktok:     '/icons/icons8-tiktok-50.png',
    exchangeArt:'/icons/icons8-rating-circled-50.png',
    mallow:     '/icons/icons8-rating-circled-50.png',
    drip:       '/icons/icons8-rating-circled-50.png',
    website:    '/icons/icons8-salt-bae-50.png',
    custom:     '/icons/icons8-salt-bae-50.png'
};

const DISPLAY_NAMES = {
    exchangeArt: 'Exchange Art',
    mallow: 'Mallow',
    drip: 'Drip',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    github: 'GitHub',
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'X',
    reddit: 'Reddit',
    pinterest: 'Pinterest',
    medium: 'Medium',
    threads: 'Threads',
    website: 'Website',
    bluesky: 'Bluesky'
};

const normalizeLinkValue = (value) => {
    if (typeof value === 'string') return { url: value, label: '' };
    if (value && typeof value === 'object') {
        return {
            url: value.url || value.link || '',
            label: value.label || value.name || ''
        };
    }
    return { url: '', label: '' };
};

const addProtocol = (rawUrl) => {
    if (!rawUrl) return '';
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    return `https://${rawUrl}`;
};

const extractLabelFromUrl = (rawUrl, key) => {
    if (!rawUrl) return '';
    let parsed;
    try {
        parsed = new URL(addProtocol(rawUrl));
    } catch {
        return '';
    }

    const hostname = (parsed.hostname || '').replace(/^www\./i, '');
    const segments = (parsed.pathname || '')
        .split('/')
        .map(s => s.trim())
        .filter(Boolean);

    if (key === 'website' || key === 'custom') {
        return hostname || rawUrl;
    }

    if (segments.length === 0) return hostname || '';

    const first = segments[0];
    if (first.startsWith('@')) return first;

    if (key === 'youtube') {
        if (first === 'channel' || first === 'c' || first === 'user') return segments[1] || first;
        return first;
    }
    if (key === 'reddit') {
        if (first === 'user' || first === 'u') return segments[1] || first;
        if (first === 'r') return `r/${segments[1] || ''}`.trim();
        return first;
    }
    if (key === 'linkedin') {
        if (first === 'in' || first === 'company' || first === 'school') return segments[1] || first;
        return first;
    }
    if (key === 'facebook') {
        if (first === 'profile.php') {
            const id = parsed.searchParams.get('id');
            return id ? `id:${id}` : first;
        }
        return first;
    }

    return first;
};

const resolveDisplayLabel = (key, rawUrl, explicitLabel) => {
    if (explicitLabel) return explicitLabel;
    const extracted = extractLabelFromUrl(rawUrl, key);
    return extracted || DISPLAY_NAMES[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Link');
};

export default function SocialLinksComponent({
                                                 comp,
                                                 index,
                                                 moveUp,
                                                 moveDown,
                                                 onEdit,
                                                 removeComponent,
                                                 isEditor = false,
                                                 // gating props
                                                 gateType,
                                                 selectedListId,
                                                 onGateTypeChange,
                                                 onGateListChange,
                                                 donationCampaignOptions = [],
                                                 donationGate,
                                                 onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,
                                                 style = {},
                                                 onStyleChange,
                                                 coverUrl = '',
                                                 onCoverChange,
                                                 onPanelBackgroundImageChange,
                                                 timestampDisplay = 'none',
                                                 onTimestampDisplayChange,
                                                 extraGates,
                                                 gateLogic,
                                                 onExtraGatesChange,
                                                 onGateLogicChange,

                                                 // NEW (optional, no renames)
                                                 pageTypography = null,
                                                 ownerPublicKey = '',
                                             }) {
    // initialize gate from component
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate  = normalizeGate(comp.gate || { type: 'public', listId: '' });
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const [gateRefreshKey, setGateRefreshKey] = useState(0);
    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const { username: routeUsername } = useParams();
    const safeUsername = routeUsername || comp?.muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);
    // global + panel typography (panel overrides win)
    const typo = resolveTypography(pageTypography, style);
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));

    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    if (isLoading) return <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
        <h3>Checking access for {gateNamesLabel}…</h3>
        <TimestampBadge
            mode={comp.timestampDisplay || 'none'}
            createdAt={comp.createdAt}
            updatedAt={comp.updatedAt}
        />
        {analyticsFooter}
        <ComponentControls
            compId={comp.id}
            index={index}
            moveUp={moveUp}
            moveDown={moveDown}
            onEdit={onEdit}
            removeComponent={removeComponent}
            gateType={gate.type}
            selectedListId={gate.listId}
            onGateTypeChange={onGateTypeChange}
            onGateListChange={onGateListChange}
            extraGates={extraGates ?? comp.extraGates ?? []}
            gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
            onExtraGatesChange={onExtraGatesChange}
            onGateLogicChange={onGateLogicChange}
            donationCampaignOptions={donationCampaignOptions}
            donationGate={donationGate ?? comp.gate}
            onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
            style={style}
            onStyleChange={onStyleChange}
            coverUrl={coverUrl}
            onCoverChange={onCoverChange}
            onPanelBackgroundImageChange={onPanelBackgroundImageChange}
            timestampDisplay={comp.timestampDisplay || 'none'}
            onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
        />

    </div>
        </GateRevealPanel>;
    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1>{deniedHeading} - {gateNamesLabel}</h1>
                {passwordGate ? (
                    <PasswordUnlock
                        gate={passwordGate}
                        componentId={comp.id}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {directPaymentGate ? (
                    <DirectPaymentUnlock
                        gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={`${basePanelClass} social-links`} style={style}>
            <h1
                style={{
                    fontFamily: typo.headers.fontFamily,
                    fontSize: numToPx(typo.headers.fontSize),
                    fontWeight: typo.headers.fontWeight,
                    color: typo.headers.color,
                    lineHeight: typo.headers.lineHeight
                }}
            >
                Social Links
            </h1>
            <ul className="social-links-list">
                {Object.entries(comp.links || {}).map(([key, value]) => {
                    const { url, label } = normalizeLinkValue(value);
                    if (!url) return null;
                    const displayLabel = resolveDisplayLabel(key, url, label);
                    const iconSrc = ICON_PATHS[key] || ICON_PATHS.website;
                    return (
                        <li key={key}>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`social-link ${key}`}
                                style={{
                                    // preserve your previous behavior (style.color) but prefer explicit link styles
                                    color: typo.links.color || style.color,
                                    textDecoration: typo.links.textDecoration || undefined
                                }}
                            >
                                <img src={iconSrc} alt={`${key} icon`} className="social-icon" />
                                <span
                                    className="social-label"
                                    style={{
                                        fontFamily: typo.paragraph.fontFamily,
                                        fontSize: numToPx(typo.paragraph.fontSize),
                                        color: typo.paragraph.color,
                                        lineHeight: typo.paragraph.lineHeight
                                    }}
                                >
                                    {displayLabel}
                                </span>
                            </a>
                        </li>
                    );
                })}
                {(Array.isArray(comp.customLinks) ? comp.customLinks : []).map((link, idx) => {
                    const { url, label } = normalizeLinkValue(link);
                    if (!url) return null;
                    const displayLabel = resolveDisplayLabel('custom', url, label);
                    return (
                        <li key={link?.id || `custom-${idx}`}>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-link custom"
                                style={{
                                    color: typo.links.color || style.color,
                                    textDecoration: typo.links.textDecoration || undefined
                                }}
                            >
                                <img src={ICON_PATHS.custom} alt="custom link icon" className="social-icon" />
                                <span
                                    className="social-label"
                                    style={{
                                        fontFamily: typo.paragraph.fontFamily,
                                        fontSize: numToPx(typo.paragraph.fontSize),
                                        color: typo.paragraph.color,
                                        lineHeight: typo.paragraph.lineHeight
                                    }}
                                >
                                    {displayLabel}
                                </span>
                            </a>
                        </li>
                    );
                })}
            </ul>
            {hasAccess && (
                <GateBadge  gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
            />
        </div>
        </GateRevealPanel>
    );
}



